import { useTranslation } from 'react-i18next';
import {
  CircleAlert,
  Gauge,
  KeyRound,
  Megaphone,
  UserRoundCheck,
  UserRoundPlus,
  UsersRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { ImportAnalysis } from '../model/types';
import styles from './Import.module.css';

interface Props {
  analysis: ImportAnalysis;
}

type Tone = 'plain' | 'blocking' | 'effect' | 'notice';

interface Tile {
  key: string;
  label: string;
  value: number;
  hint?: string;
  tone: Tone;
  icon?: LucideIcon;
}

/**
 * Panel liczb (S1) — ma odpowiadać na cztery pytania <b>bez przewijania</b>: co wejdzie ·
 * co nie wejdzie i dlaczego · <b>co się stanie przy okazji</b> · czy obraz jest aktualny.
 *
 * ⭐ <b>„Nie wejdzie, bo pominięte" i „nie wejdzie, bo nierozstrzygnięte" stoją osobno</b>
 * i to nie jest szczegół: pierwsze jest decyzją i nie blokuje, drugie blokuje zatwierdzenie.
 * Jedna wspólna liczba zrobiłaby z zatwierdzania zgadywankę.
 *
 * ⚠ <b>Kolor tylko wzmacnia</b> (decyzja 58): każdy kafel niesie treść słowem i liczbą,
 * a te, które muszą się wyróżnić, mają dodatkowo ikonę. Sam odcień nigdy nie jest jedynym
 * nośnikiem informacji.
 */
export function ImportStats({ analysis }: Props) {
  const { t, i18n } = useTranslation();
  const { counts, campaigns, elementsQuota } = analysis;
  const quotaExceeded = elementsQuota.wouldExceed ?? elementsQuota.afterCommit > elementsQuota.max;

  const format = new Intl.NumberFormat(i18n.language);
  const dosypka = campaigns.reduce((sum, campaign) => sum + campaign.commitments, 0);

  const tiles: Tile[] = [
    {
      key: 'enters',
      label: t('import.stats.enters'),
      value: counts.newElements + counts.updatedElements,
      hint: t('import.stats.entersHint', {
        relations: format.format(counts.relations),
        owners: format.format(counts.owners),
      }),
      tone: 'plain',
    },
    {
      key: 'unchanged',
      label: t('import.stats.unchanged'),
      value: counts.unchanged,
      hint: t('import.stats.unchangedHint'),
      tone: 'plain',
    },
    {
      key: 'waiting',
      label: t('import.stats.waiting'),
      value: counts.conflictsWaiting,
      hint: t('import.stats.waitingHint'),
      tone: 'blocking',
      icon: CircleAlert,
    },
    {
      key: 'resolved',
      label: t('import.stats.resolved'),
      value: counts.conflictsResolved + counts.conflictsSkipped,
      hint: t('import.stats.resolvedHint', {
        resolved: format.format(counts.conflictsResolved),
        skipped: format.format(counts.conflictsSkipped),
      }),
      tone: 'plain',
    },
  ];

  if (counts.adoptedKeys > 0) {
    // ⛔ Kafel stoi tu nie dla kompletności: wycofanie tych elementów ICH NIE USUNIE (R23),
    // a admin patrzący na wycofanie musi tę liczbę znać wcześniej.
    tiles.push({
      key: 'adopted',
      label: t('import.stats.adopted'),
      value: counts.adoptedKeys,
      hint: t('import.stats.adoptedHint'),
      tone: 'notice',
      icon: KeyRound,
    });
  }

  if (dosypka > 0) {
    tiles.push({
      key: 'campaigns',
      label: t('import.stats.campaigns'),
      value: dosypka,
      hint: campaigns.map((c) => `${c.title} +${format.format(c.commitments)}`).join(' · '),
      tone: 'effect',
      icon: Megaphone,
    });
  }

  if (counts.owners > 0) {
    tiles.push({
      key: 'owners',
      label: t('import.stats.owners'),
      value: counts.owners,
      hint: t('import.stats.ownersHint'),
      tone: 'effect',
      icon: UserRoundCheck,
    });
  }

  // ⭐⭐ Zapowiedź tego, co zatwierdzenie zrobi KATALOGOWI ORGANIZACJI (2026-09-04). Konta
  //     z importu są nieodwracalne — wycofanie przebiegu ich nie usuwa — więc liczba musi stać
  //     na ekranie ZANIM człowiek kliknie „Zatwierdź".
  // ⚠ Konta i zespoły w OSOBNYCH kafelkach, choć oba powstają w tym samym kroku: jeden kafel
  //    „nowe konta" pokazujący sumę **kłamałby** o tym, ile osób pojawi się w organizacji
  //    (znalezione okiem 2026-09-04 — liczby w panelu importu nie mogą kłamać).
  if (counts.newUsers > 0) {
    tiles.push({
      key: 'newUsers',
      label: t('import.stats.usersCreated'),
      value: counts.newUsers,
      hint: t('import.stats.usersCreatedHint'),
      tone: 'effect',
      icon: UserRoundPlus,
    });
  }

  if (counts.newTeams > 0) {
    tiles.push({
      key: 'newTeams',
      label: t('import.stats.teamsCreated'),
      value: counts.newTeams,
      hint: t('import.stats.teamsCreatedHint'),
      tone: 'effect',
      icon: UsersRound,
    });
  }

  // ⚠ Liczba nazw jest policzona NIEZALEŻNIE od opcji „nadpisuj nazwy" i dlatego jest uczciwa
  //   w obu stanach przełącznika: mówi „tyle nazw ta opcja by zmieniła", więc człowiek widzi
  //   cenę, ZANIM ją włączy.
  if (counts.displayNamesDiffer > 0) {
    tiles.push({
      key: 'namesUpdated',
      label: t('import.stats.namesUpdated'),
      value: counts.displayNamesDiffer,
      hint: t('import.stats.namesUpdatedHint'),
      tone: 'plain',
      icon: UserRoundCheck,
    });
  }

  // ⭐ Kontyngent elementów tenanta (dec. 154): kafel ZAWSZE, gdy wsad dokłada elementy —
  //    liczba „po zatwierdzeniu" jest liczona tym samym warunkiem, co commit, więc admin widzi
  //    PRZED kliknięciem, czy graf to zmieści. Ponad limit = ton blokujący z ikoną (kolor tylko
  //    wzmacnia, dec. 58); serwer i tak odmówi pod blokadą, ale ekran ma powiedzieć to pierwszy.
  if (counts.newElements > 0 || quotaExceeded) {
    tiles.push({
      key: 'quota',
      label: t('import.stats.quota'),
      value: elementsQuota.afterCommit,
      hint: quotaExceeded
        ? t('import.stats.quotaExceededHint', { max: format.format(elementsQuota.max) })
        : t('import.stats.quotaHint', { max: format.format(elementsQuota.max) }),
      tone: quotaExceeded ? 'blocking' : 'plain',
      icon: quotaExceeded ? CircleAlert : Gauge,
    });
  }

  return (
    <ul className={styles['tiles']} aria-label={t('import.stats.label')}>
      {tiles.map((tile) => {
        const Icon = tile.icon;

        return (
          <li key={tile.key} className={styles['tile']} data-tone={tile.tone}>
            <span className={styles['tileLabel']}>
              {Icon ? <Icon size={13} aria-hidden="true" /> : null}
              {tile.label}
            </span>
            <span className={styles['tileValue']}>{format.format(tile.value)}</span>
            {tile.hint ? <span className={styles['tileHint']}>{tile.hint}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}
